/**
 * Admin Users oversight panel (minimal). Lists users; selecting one shows a
 * simple detail view (profile + recent chat). AutoDieta nutrition domain only.
 *
 * RENDER GATE: mounted only inside AdminPanel (admin-claim gated), and the Worker
 * independently enforces the admin claim on every request.
 */

import { useState } from "react";
import { useAdminUsers, useAdminUserDetail, type AdminHooksDeps } from "./hooks.js";

export interface UsersPanelProps {
  deps: AdminHooksDeps;
}

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat("es-AR", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function UsersPanel({ deps }: UsersPanelProps): JSX.Element {
  const { users, loading, error, reload } = useAdminUsers(deps);
  const [selectedUid, setSelectedUid] = useState<string | null>(null);

  if (loading && users.length === 0) {
    return (
      <p role="status" aria-live="polite" className="flex items-center gap-3 text-sm text-zinc-400">
        <span
          aria-hidden="true"
          className="h-4 w-4 animate-spin rounded-full border-2 border-zinc-700 border-t-blue-400"
        />
        Cargando usuarios…
      </p>
    );
  }
  if (error) {
    return (
      <p role="alert" className="text-sm text-red-400">
        {error}
      </p>
    );
  }

  if (selectedUid) {
    return (
      <UserDetail
        deps={deps}
        uid={selectedUid}
        onBack={() => {
          setSelectedUid(null);
          void reload();
        }}
      />
    );
  }

  return (
    <section aria-label="admin-users" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-semibold text-zinc-100">Usuarios</h2>
        <button
          type="button"
          aria-label="reload-users"
          className="min-h-[44px] rounded-lg border border-zinc-700 px-3 py-1.5 text-xs font-medium text-zinc-300 transition-colors hover:bg-zinc-800"
          onClick={() => void reload()}
        >
          Recargar
        </button>
      </div>
      {users.length === 0 ? (
        <p className="rounded-lg border border-dashed border-zinc-800 px-4 py-8 text-center text-sm text-zinc-500">
          No se encontraron usuarios.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-zinc-800">
          <table className="w-full text-left text-sm">
            <thead className="bg-zinc-950/60 text-xs uppercase tracking-wide text-zinc-500">
              <tr>
                <th className="px-3 py-2 font-medium">Email</th>
                <th className="px-3 py-2 font-medium">Nombre</th>
                <th className="px-3 py-2 font-medium">Creado</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {users.map((u) => (
                <tr
                  key={u.uid}
                  data-testid={`user-row-${u.uid}`}
                  className="text-zinc-300 transition-colors hover:bg-zinc-800/40"
                >
                  <td className="px-3 py-2 text-zinc-100">{u.email}</td>
                  <td className="px-3 py-2">{u.displayName}</td>
                  <td className="px-3 py-2 text-zinc-400">{formatDate(u.createdAt)}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      aria-label={`view-user-${u.uid}`}
                      className="min-h-[44px] rounded-lg border border-zinc-700 px-3 py-1 text-xs font-medium text-zinc-300 transition-colors hover:bg-zinc-800"
                      onClick={() => setSelectedUid(u.uid)}
                    >
                      Ver
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

interface UserDetailProps {
  deps: AdminHooksDeps;
  uid: string;
  onBack: () => void;
}

function UserDetail({ deps, uid, onBack }: UserDetailProps): JSX.Element {
  const { detail, loading, error } = useAdminUserDetail(deps, uid);

  return (
    <section aria-label={`admin-user-detail-${uid}`} className="space-y-4">
      <button
        type="button"
        aria-label="back-to-users"
        className="min-h-[44px] rounded-lg px-0 text-sm text-blue-400 hover:text-blue-300"
        onClick={onBack}
      >
        ← Volver
      </button>

      {loading && !detail ? (
        <p role="status" aria-live="polite" className="flex items-center gap-3 text-sm text-zinc-400">
          <span
            aria-hidden="true"
            className="h-4 w-4 animate-spin rounded-full border-2 border-zinc-700 border-t-blue-400"
          />
          Cargando usuario…
        </p>
      ) : error ? (
        <p role="alert" className="text-sm text-red-400">
          {error}
        </p>
      ) : (
        <>
          <h2 className="text-base font-semibold text-zinc-100">
            {detail?.profile?.displayName || uid}
          </h2>

          <dl
            data-testid="user-profile"
            className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 rounded-lg border border-zinc-800 bg-zinc-950/40 p-4 text-sm"
          >
            <dt className="text-zinc-500">Email</dt>
            <dd className="text-zinc-200">{detail?.profile?.email ?? "—"}</dd>
            <dt className="text-zinc-500">Creado</dt>
            <dd className="text-zinc-200">
              {detail?.profile?.createdAt ? formatDate(detail.profile.createdAt) : "—"}
            </dd>
            {detail?.profile?.lastLoginAt ? (
              <>
                <dt className="text-zinc-500">Último login</dt>
                <dd className="text-zinc-200">{formatDate(detail.profile.lastLoginAt)}</dd>
              </>
            ) : null}
            {detail?.profile?.loginCount != null ? (
              <>
                <dt className="text-zinc-500">Inicios de sesión</dt>
                <dd className="text-zinc-200">{detail.profile.loginCount}</dd>
              </>
            ) : null}
          </dl>

          <h3 className="text-sm font-medium text-zinc-400">Chat reciente ({detail?.recentChat.length ?? 0})</h3>
          <ul data-testid="user-chat" className="space-y-1 text-sm text-zinc-300">
            {(detail?.recentChat ?? []).map((c) => (
              <li key={c.id} className="rounded-md bg-zinc-900 px-3 py-1.5">
                <strong className="text-zinc-100">{c.role}:</strong>{" "}
                <span>{c.preview}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
